import { json } from "@remix-run/node";
import {
  Outlet,
  useRouteError,
  useLoaderData,
  useNavigate,
} from "@remix-run/react";
import { useEffect } from "react";
import { withSentry, captureRemixErrorBoundaryError } from "@sentry/remix";
import { getBrowserUser } from "#app/utils/session.server.js";
import { getFlashMessage } from "#app/utils/flash.server.js";
import { createTiming } from "#app/utils/timing.server.js";
import { withSecurityHeaders } from "#app/utils/security.server.js";
import { parseLocaleFromHeader } from "#app/utils/resolveLocale.js";
import { getCurrencyFromCountry, getCountryCode } from "#app/config/stripe.js";
import {
  ChakraProvider,
  cookieStorageManagerSSR,
  useConst,
  Button,
  Link,
  Text,
  Code,
} from "@chakra-ui/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { ErrorMessage } from "#app/components/index.jsx";
import Layout, { StaticLayout } from "./layout.jsx";
import Document from "./document.jsx";
import theme from "./theme/index.js";
import { useColorMode } from "#app/hooks/index.js";
import { getColorModeCookie } from "#app/hooks/useColorMode.js";
import { getSeoMeta, getBaseUrl, getDefaultOgImage } from "#app/utils/meta.js";
import { LocaleProvider } from "#app/context/LocaleContext.jsx";
import { GeoDebugBanner } from "#app/components/GeoDebugBanner.jsx";
import {
  identifyPostHogUser,
  resetPostHog,
} from "#app/utils/posthog.client.js";

import globalStyles from "#app/styles/global.css?url";
import rdpStyles from "react-day-picker/style.css?url";
import rdpOverrideStyles from "#app/styles/rdpOverride.css?url";

export function meta({ matches }) {
  const baseUrl = getBaseUrl(matches);
  return getSeoMeta({
    title: "Campground fully booked? Schnerp it | Schnerp",
    url: baseUrl,
    image: getDefaultOgImage(baseUrl),
  });
}
export function links() {
  return [
    {
      rel: "stylesheet",
      href: globalStyles,
    },
    {
      rel: "stylesheet",
      href: rdpStyles,
    },
    {
      rel: "stylesheet",
      href: rdpOverrideStyles,
    },
  ];
}

export function headers({ loaderHeaders }) {
  return withSecurityHeaders(loaderHeaders);
}

export async function loader({ request }) {
  const { time, getHeader } = createTiming();

  const user = await time("user", () => getBrowserUser(request));
  const { flashMessage, headers } = await time("flash", () =>
    getFlashMessage(request),
  );

  // Locale: use user's saved preference, or detect from Accept-Language header
  const locale =
    user?.locale ||
    parseLocaleFromHeader(request.headers.get("Accept-Language"));

  const countryCode = getCountryCode(request);
  const pricingCurrency = getCurrencyFromCountry(countryCode);
  const detectedCountry = countryCode || null;

  return json(
    {
      env: {
        APP_ENV: process.env.APP_ENV,
        BASE_URL: process.env.BASE_URL,
        REWARDFUL_PUBLIC_KEY: process.env.REWARDFUL_PUBLIC_KEY,
        SENTRY_DSN: process.env.SENTRY_DSN,
        GIT_COMMIT_SHA: process.env.GIT_COMMIT_SHA || "development",
        VAPID_PUBLIC_KEY: process.env.VAPID_PUBLIC_KEY,
        // Public PostHog project token + hosts for the client SDK (posthog.client.js).
        // POSTHOG_PROXY_HOST is the first-party managed reverse proxy the browser
        // SDK sends to (ad-block resistant); POSTHOG_HOST is the direct fallback.
        POSTHOG_PUBLIC_KEY: process.env.POSTHOG_PUBLIC_KEY,
        POSTHOG_HOST: process.env.POSTHOG_HOST,
        POSTHOG_PROXY_HOST: process.env.POSTHOG_PROXY_HOST,
      },
      user,
      locale,
      pricingCurrency,
      detectedCountry,
      flashMessage,
      colorModeCookie: getColorModeCookie(request.headers.get("cookie") ?? ""),
    },
    {
      headers: {
        ...headers,
        "Server-Timing": getHeader(),
      },
    },
  );
}

export async function action() {
  // Only used to reload the page
  return null;
}

export const ErrorBoundary = () => {
  const error = useRouteError();
  const navigate = useNavigate();
  let { colorMode, cookies } = useColorMode();

  if (error?.status === 404) {
    return (
      <Document colorMode={colorMode}>
        <ChakraProvider
          theme={theme}
          colorModeManager={cookieStorageManagerSSR(cookies)}
        >
          <StaticLayout
            ActionButton={
              <Button variant="primary" onClick={() => navigate(-1)}>
                Go back
              </Button>
            }
          >
            <ErrorMessage
              status="warning"
              title="Page not found"
              description={
                <>
                  <Text>The page you're trying to access does not exist.</Text>
                  <Text mt={4}>
                    If you need any help, please email{" "}
                    <Link
                      isExternal
                      href="mailto:help@schnerp.com?subject=Please%20help"
                    >
                      help@schnerp.com
                    </Link>{" "}
                    for assistance.
                  </Text>
                </>
              }
            />
          </StaticLayout>
        </ChakraProvider>
      </Document>
    );
  }

  if (error?.data?.error) {
    return (
      <Document colorMode={colorMode}>
        <ChakraProvider
          theme={theme}
          colorModeManager={cookieStorageManagerSSR(cookies)}
        >
          <StaticLayout
            ActionButton={
              <Button variant="primary" onClick={() => navigate(-1)}>
                Go back
              </Button>
            }
          >
            <ErrorMessage
              status="warning"
              title="Uh oh!"
              description={
                <>
                  <Text>{error.data.error}</Text>
                  <Text mt={4}>
                    If you need any help, please email{" "}
                    <Link
                      isExternal
                      href="mailto:help@schnerp.com?subject=Please%20help"
                    >
                      help@schnerp.com
                    </Link>{" "}
                    for assistance.
                  </Text>
                </>
              }
            />
          </StaticLayout>
        </ChakraProvider>
      </Document>
    );
  }

  captureRemixErrorBoundaryError(error);

  return (
    <Document colorMode={colorMode}>
      <ChakraProvider
        theme={theme}
        colorModeManager={cookieStorageManagerSSR(cookies)}
      >
        <StaticLayout
          ActionButton={
            <Button variant="primary" onClick={() => window.location.reload()}>
              Reload
            </Button>
          }
        >
          <ErrorMessage
            title="Sorry, something went wrong"
            description={
              <>
                <Code my={6}>{error.message}</Code>
                <Text>
                  Our team has been notified of this error. Please reload the
                  page and try again.
                </Text>
                <Text mt={4}>
                  If the problem continues, please email{" "}
                  <Link
                    isExternal
                    href="mailto:help@schnerp.com?subject=Please%20help"
                  >
                    help@schnerp.com
                  </Link>{" "}
                  for assistance.
                </Text>
                <Button
                  mt={4}
                  variant="primary"
                  onClick={() => window.location.reload()}
                >
                  Reload
                </Button>
              </>
            }
          />
        </StaticLayout>
      </ChakraProvider>
    </Document>
  );
};

const queryClient = new QueryClient();

function App() {
  let { env, user, locale, colorModeCookie } = useLoaderData();

  // Identify on the client (covers magic-link and passkey logins alike, since
  // neither has a client "login moment"); reset on logout. Server-side identify
  // can't merge the anonymous session, so this is the only place it works.
  useEffect(() => {
    if (user) {
      identifyPostHogUser(user);
    } else {
      resetPostHog();
    }
  }, [user?.id]);

  let { colorMode } = useColorMode(colorModeCookie);

  const colorModeManager = useConst(cookieStorageManagerSSR(colorModeCookie));

  return (
    <Document env={env} user={user} colorMode={colorMode}>
      <ChakraProvider theme={theme} colorModeManager={colorModeManager}>
        <LocaleProvider locale={locale}>
          <QueryClientProvider client={queryClient}>
            {/* <GeoDebugBanner /> */}
            <Layout>
              <Outlet />
            </Layout>
          </QueryClientProvider>
        </LocaleProvider>
      </ChakraProvider>
    </Document>
  );
}

export default withSentry(App);
